import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { AlertTriangle, Container, GitMerge, Handshake, KeyRound, Map as MapIcon, Package, UserPlus, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { approvals, type Approval, type ApprovalSubjectType } from '../../api/tasks';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card } from '../../components/Surface';
import { ErrorBanner } from '../../components/ErrorBanner';
import { fieldStyles } from '../../components/styles';
import { useFormat } from '../../i18n/format';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';

/**
 * La tarjeta polimórfica de `13 §4`.
 *
 * La carga útil cambia según el tipo de sujeto y la tarjeta no finge conocer
 * todas las formas: las entradas escalares se muestran como lista de
 * definiciones, que es lo que la decisión necesita —`files_changed: 12`,
 * `ci: success`— sin un renderizador por tipo que acabaría por detrás del del
 * backend. La frase de resumen de encima es la parte que `09 §5.3` garantiza
 * que nunca está vacía.
 *
 * Los iconos pasaron de emoji a trazo: el emoji lo pinta la fuente del sistema
 * y en Windows la mitad de estos ocho salían con otro color y otro peso, así
 * que la fila no se leía como una sola familia.
 */

const SUBJECT_ICON: Record<ApprovalSubjectType, LucideIcon> = {
  task_plan: MapIcon,
  pr_merge: GitMerge,
  task_high_risk: AlertTriangle,
  employee_hire: UserPlus,
  employee_capability: KeyRound,
  company_agreement: Handshake,
  budget_increase: Wallet,
  repo_create: Package,
  jenkins_operation: Package,
  kubernetes_operation: Container,
};

export function ApprovalCard({
  approval,
  companyName,
  selected,
  onSelect,
  onDecided,
}: {
  approval: Approval;
  /** Solo la bandeja global lo pasa: dentro de una empresa nombrarla en cada tarjeta sería dudar del ámbito. */
  companyName?: string | undefined;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onDecided: () => void;
}) {
  const t = useT();
  const format = useFormat();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const Icon = SUBJECT_ICON[approval.subject_type] ?? MapIcon;
  const needsJenkinsDetail = (approval.subject_type === 'jenkins_operation' || approval.subject_type === 'kubernetes_operation') && !approval.payload;
  const detail = useQuery({ queryKey: ['approvals', approval.id], queryFn: () => approvals.get(approval.id), enabled: needsJenkinsDetail });
  const payload = approval.payload ?? detail.data?.payload ?? {};

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      decision === 'approve' ? approvals.approve(approval.id) : approvals.reject(approval.id, reason.trim()),
    onSuccess: onDecided,
  });

  return (
    <Card className="space-y-3">
      <header className="flex flex-wrap items-center gap-2">
        <span className="grid size-8 shrink-0 place-items-center rounded-sm bg-primary-pale text-primary">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <h3 className="font-semibold text-ink">{approval.title}</h3>
        {approval.blocking ? (
          <Badge tone="warning" size="sm">
            {t('approvals.blocksTask')}
          </Badge>
        ) : null}
        <span className="ml-auto text-xs text-ink-faint">
          {format.dateTime(approval.created_at)}
          {companyName !== undefined && companyName !== '' ? ` · ${companyName}` : ''}
        </span>
      </header>

      <p className="text-sm text-ink">{approval.subject_type === 'jenkins_operation' ? t('jenkins.approvalHint') : approval.subject_type === 'kubernetes_operation' ? t('kubernetes.approval.hint') : approval.summary}</p>

      {approval.subject_type === 'jenkins_operation' ? <JenkinsApprovalFacts payload={payload} summary={approval.summary} /> : approval.subject_type === 'kubernetes_operation' ? <KubernetesApprovalFacts payload={payload} summary={approval.summary} /> : <PayloadFacts payload={payload} />}
      {needsJenkinsDetail && detail.error ? <ErrorBanner error={detail.error} onRetry={() => void detail.refetch()} /> : null}

      {approval.expires_at !== null ? (
        <p className="text-xs text-ink-faint">
          {t('approvals.expires', { date: format.dateTime(approval.expires_at) })}
        </p>
      ) : null}

      {rejecting ? (
        <div className="space-y-2">
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={2}
            placeholder={t('approvals.rejectPlaceholder')}
            className={cn(fieldStyles, 'border-line')}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              busy={decide.isPending}
              disabled={reason.trim() === ''}
              onClick={() => decide.mutate('reject')}
            >
              {t('approvals.rejectWithReason')}
            </Button>
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              {t('common.back')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {approval.bulk_approvable ? (
            <label className="mr-auto flex cursor-pointer items-center gap-2 text-xs text-ink-soft">
              <input
                type="checkbox"
                checked={selected}
                onChange={(event) => onSelect(event.target.checked)}
                className="size-4 accent-primary"
              />
              {t('approvals.selectForBulk')}
            </label>
          ) : (
            <span className="mr-auto" />
          )}

          <Button variant="danger-ghost" onClick={() => setRejecting(true)}>
            {t('approvals.reject')}
          </Button>
          <Button busy={decide.isPending} disabled={needsJenkinsDetail && !detail.data} onClick={() => decide.mutate('approve')}>
            {t('approvals.approve')}
          </Button>
        </div>
      )}

      {decide.error !== null ? <ErrorBanner error={decide.error} /> : null}
    </Card>
  );
}

function JenkinsApprovalFacts({ payload, summary }: { payload: Record<string, unknown>; summary: string }) {
  const t = useT();
  const facts: [string, string][] = [[t('jenkins.selected'), String(payload.pipeline ?? '')]];
  let parameters: Record<string, unknown> = {};
  try {
    const parsed: unknown = typeof payload.parameters_json === 'string' ? JSON.parse(payload.parameters_json) : null;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return <p className="break-all text-sm">{summary}</p>;
    parameters = parsed as Record<string, unknown>;
  } catch { return <p className="break-all text-sm">{summary}</p>; }
  for (const [key, value] of Object.entries(parameters)) {
    if (typeof value === 'string' || typeof value === 'boolean') facts.push([key, String(value)]);
  }
  return <div className="space-y-2 rounded-sm bg-muted p-3 text-sm">
    <strong>{payload.action === 'cancel' ? t('jenkins.cancelBuild') : t('jenkins.launch')}{typeof payload.build_number === 'number' ? ` #${String(payload.build_number)}` : typeof payload.queue_id === 'number' ? ` · ${t('jenkins.queue')} #${String(payload.queue_id)}` : ''}</strong>
    {payload.scope === 'company' ? <p className="text-xs text-ink-soft">{t('jenkins.companyScope')}</p> : null}
    <dl className="space-y-2">{facts.map(([label, value]) => <div key={label} className="min-w-0"><dt className="text-xs text-ink-soft">{label}</dt><dd className="whitespace-pre-wrap break-all font-mono">{value}</dd></div>)}</dl>
  </div>;
}

function KubernetesApprovalFacts({ payload, summary }: { payload: Record<string, unknown>; summary: string }) {
  const t = useT();
  const action = typeof payload.action === 'string' ? payload.action : '';
  const target = typeof payload.target === 'object' && payload.target !== null ? (payload.target as Record<string, unknown>) : {};
  const detail = typeof payload.payload === 'object' && payload.payload !== null ? (payload.payload as Record<string, unknown>) : {};
  const facts: [string, string][] = [];
  if (typeof payload.context === 'string') facts.push([t('kubernetes.context'), payload.context]);
  if (typeof target.kind === 'string') facts.push([t('kubernetes.approval.target'), `${target.kind}/${String(target.name ?? '')} · ${String(target.namespace ?? '')}`]);
  if (typeof detail.replicas === 'number') facts.push([t('kubernetes.actions.replicas'), String(detail.replicas)]);
  if (typeof detail.digest === 'string') facts.push(['digest', detail.digest.slice(0, 12)]);
  const manifest = typeof detail.manifest_yaml === 'string' ? detail.manifest_yaml : null;
  const label = action === 'scale' || action === 'rollout_restart' || action === 'delete_pod' || action === 'suspend_cronjob' || action === 'resume_cronjob' || action === 'run_cronjob' || action === 'apply' || action === 'delete' ? t(`kubernetes.action.${action}`) : summary;
  return <div className="space-y-2 rounded-sm bg-muted p-3 text-sm">
    <strong>{label}</strong>
    <dl className="space-y-2">{facts.map(([key, value]) => <div key={key} className="min-w-0"><dt className="text-xs text-ink-soft">{key}</dt><dd className="whitespace-pre-wrap break-all font-mono">{value}</dd></div>)}</dl>
    {manifest !== null ? <details><summary className="cursor-pointer text-xs text-ink-soft">{t('kubernetes.approval.manifest')}</summary><pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-sm bg-[#101820] p-3 font-mono text-xs text-[#d5e6df]">{manifest}</pre></details> : null}
  </div>;
}

function PayloadFacts({ payload }: { payload: Record<string, unknown> }) {
  const facts = Object.entries(payload).filter(
    ([, value]) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean',
  );

  if (facts.length === 0) return null;

  return (
    /*
      Las claves son las del contrato de la API y se pintan tal cual, en
      inglés y en monoespaciada: son identificadores (`00-README §4.1`), no
      prosa, y traducirlas rompería la correspondencia con lo que el agente
      dejó escrito en la traza.
    */
    <dl className="flex flex-wrap gap-x-5 gap-y-1.5 rounded-sm bg-muted px-3 py-2 text-xs text-ink-soft">
      {facts.map(([key, value]) => (
        <div key={key} className="flex gap-1.5">
          <dt className="font-mono">{key}</dt>
          <dd className="font-semibold text-ink">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
